import React, { useState } from 'react';
import { Link, useParams } from 'react-router';
import { PageHero } from '../../components/PageHero';
import { Section } from '../../components/Section';
import { Card } from '../../components/Card';
import { Button } from '../../components/Button';
import { IconWrap } from '../../components/IconWrap';
import useTheme from '../../hooks/useTheme';
import { megaGreenData } from '../../data/data';
import { BiCheckCircle, BiTimeFive, BiClipboard, BiTrendingUp, BiHelpCircle } from 'react-icons/bi';
import { HiOutlineCheckCircle, HiOutlineClock } from 'react-icons/hi';
import { IoMdCheckmark } from 'react-icons/io';

export const ServiceDetail: React.FC = () => {
    const { theme, breakpoint } = useTheme();
    const params = useParams<{ serviceId: string }>();
    const serviceId = params.serviceId || 'assessments';

    // detailPages is an object, not an array
    const serviceDetail = (megaGreenData.services.detailPages as any)[serviceId];
    if (!serviceDetail) {
        return (
            <div style={{ padding: '48px', textAlign: 'center' }}>
                <h1>Service not found</h1>
                <Link to="/services">Back to Services</Link>
            </div>
        );
    }

    // Handle different property names
    const faqs = serviceDetail.faqs?.map((faq: any) => ({
        question: faq.q || faq.question,
        answer: faq.a || faq.answer,
    })) || [];

    const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

    // Find related projects
    const relatedProjects = megaGreenData.projects.projects
        .filter(p => p.primaryServiceLine.toLowerCase().includes(serviceId.toLowerCase()))
        .slice(0, 8);

    return (
        <>
            <PageHero
                title={serviceDetail.hero.title}
                subtitle={serviceDetail.hero.subtitle}
            />

            <Section>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: breakpoint.xs || breakpoint.sm ? '1fr' : '1fr 1fr',
                        gap: '48px',
                        alignItems: 'center',
                    }}
                >
                    {/* <div>
                        <PlaceholderImage
                            label={serviceDetail.hero.imageAlt}
                            height={300}
                        />
                    </div> */}
                    <div
                        style={{
                            display: 'flex',
                            gap: '16px',
                            flexWrap: 'wrap',
                        }}
                    >
                        <Link to="/contact" style={{ textDecoration: 'none' }}>
                            <Button variant="primary">Request a Consultation</Button>
                        </Link>
                        <Link to="/projects" style={{ textDecoration: 'none' }}>
                            <Button variant="secondary">View Projects</Button>
                        </Link>
                    </div>
                </div>
            </Section>

            <Section
                style={{
                    backgroundColor: theme.background.panel,
                }}
            >
                <h2
                    style={{
                        fontSize: breakpoint.xs || breakpoint.sm ? '28px' : '34px',
                        fontWeight: 700,
                        color: theme.text.primary,
                        marginBottom: '32px',
                    }}
                >
                    What we deliver
                </h2>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: breakpoint.xs || breakpoint.sm ? '1fr' : 'repeat(2, 1fr)',
                        gap: '24px',
                    }}
                >
                    {serviceDetail.whatWeDeliver?.map((item: any, idx: number) => (
                        <Card key={idx} hoverable variant={idx % 2 === 0 ? 'primary' : 'secondary'}
                            style={{ flex: 1 }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '12px',
                                }}
                            >
                                <IconWrap
                                    icon={<BiCheckCircle />}
                                    size={24}
                                    color={idx % 2 === 0 ? 'primary' : 'secondary'}
                                    withBackground={true}
                                    style={{ flexShrink: 0, marginTop: '2px' }}
                                />
                                <div style={{ flex: 1 }}>
                                    <h3
                                        style={{
                                            fontSize: '18px',
                                            fontWeight: 600,
                                            color: theme.text.primary,
                                            marginBottom: '8px',
                                        }}
                                    >
                                        {item.title}
                                    </h3>
                                    <p
                                        style={{
                                            fontSize: '14px',
                                            color: theme.text.muted,
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {item.desc || item.description}
                                    </p>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            </Section>

            <Section>
                <h2
                    style={{
                        fontSize: breakpoint.xs || breakpoint.sm ? '28px' : '34px',
                        fontWeight: 700,
                        color: theme.text.primary,
                        marginBottom: '32px',
                    }}
                >
                    What we need from you (to start fast)
                </h2>
                <Card>
                    <ul
                        style={{
                            listStyle: 'none',
                            padding: 0,
                            margin: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                        }}
                    >
                        {serviceDetail.whatWeNeedFromYou?.map((item: string, idx: number) => (
                            <li
                                key={idx}
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '12px',
                                    fontSize: '16px',
                                    color: theme.text.primary,
                                }}
                            >
                                <IconWrap
                                    icon={<IoMdCheckmark />}
                                    size={20}
                                    color="primary"
                                    style={{ marginTop: '2px', flexShrink: 0 }}
                                />
                                <span>{item}</span>
                            </li>
                        ))}
                    </ul>
                </Card>
            </Section>

            <Section
                style={{
                    backgroundColor: theme.background.panel,
                }}
            >
                <h2
                    style={{
                        fontSize: breakpoint.xs || breakpoint.sm ? '28px' : '34px',
                        fontWeight: 700,
                        color: theme.text.primary,
                        marginBottom: '32px',
                    }}
                >
                    Typical timeline
                </h2>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                    }}
                >
                    {serviceDetail.typicalTimeline?.map((phase: any, idx: number) => {
                        const timelineIcons = [
                            <BiTimeFive />,
                            <BiClipboard />,
                            <BiTrendingUp />,
                            <HiOutlineCheckCircle />,
                        ];
                        const timelineColors: Array<'primary' | 'secondary' | 'accent' | 'highlight'> = [
                            'primary',
                            'secondary',
                            'accent',
                            'highlight',
                        ];

                        return (
                            <Card key={idx} hoverable variant={idx % 2 === 0 ? 'primary' : 'secondary'}>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '16px',
                                    }}
                                >
                                    <IconWrap
                                        icon={timelineIcons[idx] || <HiOutlineClock />}
                                        size={32}
                                        color={timelineColors[idx] || 'primary'}
                                        withBackground={true}
                                        style={{ flexShrink: 0 }}
                                    />
                                    <div style={{ flex: 1 }}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: '8px',
                                                flexWrap: 'wrap',
                                                gap: '8px',
                                            }}
                                        >
                                            <h3
                                                style={{
                                                    fontSize: '18px',
                                                    fontWeight: 600,
                                                    color: theme.text.primary,
                                                }}
                                            >
                                                {phase.phase}
                                            </h3>
                                            <div
                                                style={{
                                                    fontSize: '14px',
                                                    fontWeight: 600,
                                                    color: theme.colors[timelineColors[idx] || 'primary'],
                                                    padding: '4px 10px',
                                                    backgroundColor: `${theme.colors[timelineColors[idx] || 'primary']}15`,
                                                    borderRadius: '8px',
                                                }}
                                            >
                                                {phase.duration}
                                            </div>
                                        </div>
                                        <p
                                            style={{
                                                fontSize: '14px',
                                                color: theme.text.muted,
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            {phase.notes}
                                        </p>
                                    </div>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            </Section>

            {relatedProjects.length > 0 && (
                <Section>
                    <h2
                        style={{
                            fontSize: breakpoint.xs || breakpoint.sm ? '28px' : '34px',
                            fontWeight: 700,
                            color: theme.text.primary,
                            marginBottom: '32px',
                        }}
                    >
                        Related projects (selected)
                    </h2>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: breakpoint.xs || breakpoint.sm ? '1fr' : breakpoint.md ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                            gap: '24px',
                        }}
                    >
                        {relatedProjects.map((project) => (
                            <Card key={project.id} hoverable style={{ display: 'flex', flexDirection: 'column' }}>
                                <div
                                    style={{
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        color: theme.palette.primary.main,
                                        marginBottom: '4px',
                                    }}
                                >
                                    {project.id}
                                </div>
                                <h3
                                    style={{
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        color: theme.text.primary,
                                        marginBottom: '4px',
                                    }}
                                >
                                    {project.client}
                                </h3>
                                <p
                                    style={{
                                        fontSize: '13px',
                                        color: theme.text.muted,
                                        marginBottom: '12px',
                                    }}
                                >
                                    {project.location}
                                </p>
                                <Link to={`/projects/${project.id}`} style={{ textDecoration: 'none', marginTop: 'auto' }}>
                                    <Button variant="secondary" style={{ width: '100%' }}>
                                        View case study
                                    </Button>
                                </Link>
                            </Card>
                        ))}
                    </div>
                </Section>
            )}

            <Section
                style={{
                    backgroundColor: theme.background.panel,
                }}
            >
                <h2
                    style={{
                        fontSize: breakpoint.xs || breakpoint.sm ? '28px' : '34px',
                        fontWeight: 700,
                        color: theme.text.primary,
                        marginBottom: '32px',
                    }}
                >
                    FAQ
                </h2>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                    }}
                >
                    {faqs.map((faq: { question: string; answer: string }, idx: number) => (
                        <Card
                            key={idx}
                            hoverable
                            onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                            style={{ cursor: 'pointer' }}
                            variant={idx % 2 === 0 ? 'primary' : 'secondary'}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: '12px',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        flex: 1,
                                    }}
                                >
                                    <IconWrap
                                        icon={<BiHelpCircle />}
                                        size={24}
                                        color={idx % 2 === 0 ? 'primary' : 'secondary'}
                                        withBackground={true}
                                        style={{ flexShrink: 0 }}
                                    />
                                    <h3
                                        style={{
                                            fontSize: '16px',
                                            fontWeight: 600,
                                            color: theme.text.primary,
                                        }}
                                    >
                                        {faq.question}
                                    </h3>
                                </div>
                                <div
                                    style={{
                                        fontSize: '24px',
                                        color: theme.colors.primary,
                                        fontWeight: 300,
                                        width: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '50%',
                                        backgroundColor: `${theme.colors.primary}10`,
                                        flexShrink: 0,
                                    }}
                                >
                                    {expandedFaq === idx ? '−' : '+'}
                                </div>
                            </div>
                            {expandedFaq === idx && (
                                <p
                                    style={{
                                        fontSize: '14px',
                                        color: theme.text.muted,
                                        marginTop: '12px',
                                        lineHeight: 1.5,
                                    }}
                                >
                                    {faq.answer}
                                </p>
                            )}
                        </Card>
                    ))}
                </div>
            </Section>

            <Section
                style={{
                    backgroundColor: theme.background.panel,
                }}
            >
                <div
                    style={{
                        maxWidth: '800px',
                        margin: '0 auto',
                        textAlign: 'center',
                    }}
                >
                    <h2
                        style={{
                            fontSize: breakpoint.xs || breakpoint.sm ? '28px' : '34px',
                            fontWeight: 700,
                            color: theme.text.primary,
                            marginBottom: '16px',
                        }}
                    >
                        Tell us what you're planning
                    </h2>
                    <p
                        style={{
                            fontSize: '16px',
                            color: theme.text.muted,
                            marginBottom: '24px',
                        }}
                    >
                        {megaGreenData.contact.pageIntro}
                    </p>
                    <Link to="/contact" style={{ textDecoration: 'none' }}>
                        <Button variant="primary" style={{ padding: '12px 32px' }}>
                            Request a Consultation
                        </Button>
                    </Link>
                </div>
            </Section>
        </>
    );
};

